import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { UPLOAD_MAX_FILE_SIZE_BYTES, UPLOAD_MAX_FILE_SIZE_LABEL } from "@/config";
import { AuthPage } from "@/features/auth/auth-page";
import { DropZone } from "@/features/upload/drop-zone";
import { FileQueue } from "@/features/upload/file-queue";
import { useUploadQueue } from "@/features/upload/use-upload-queue";
import { useUpload } from "@/features/upload/use-upload";

function formatBytes(n: number) {
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse" aria-busy="true" aria-label="Loading">
      <div className="space-y-3">
        <div className="h-9 w-2/3 rounded-md bg-ink/10" />
        <div className="h-5 w-full max-w-xl rounded-md bg-ink/8" />
        <div className="h-5 w-4/5 max-w-lg rounded-md bg-ink/8" />
      </div>
      <div className="h-40 rounded-lg border-2 border-dashed border-ink/10 bg-cream/60" />
    </div>
  );
}

export function UploadPage() {
  const [email, setEmail] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [invalidLink, setInvalidLink] = useState(false);
  const [note, setNote] = useState("");
  const queue = useUploadQueue(UPLOAD_MAX_FILE_SIZE_BYTES);
  const upload = useUpload();

  useEffect(() => {
    api
      .me()
      .then((r) => setEmail(r.authenticated ? (r.email ?? null) : null))
      .catch(() => setEmail(null))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("error") === "invalid_link") {
      setInvalidLink(true);
      window.history.replaceState({}, "", "/");
    }
  }, []);

  const uploading = upload.phase === "starting" || upload.phase === "uploading" || upload.phase === "finishing";

  const handleUpload = async () => {
    if (!queue.readyFiles.length) return;
    await upload.start(queue.readyFiles, note.trim() || undefined);
  };

  const logout = async () => {
    await api.logout();
    setEmail(null);
    queue.clear();
    upload.reset();
  };

  if (checking) {
    return <LoadingSkeleton />;
  }

  if (!email) {
    return (
      <>
        {invalidLink && (
          <div
            className="mb-6 rounded-md border border-error/20 bg-error/10 px-4 py-3 text-body-sm text-error"
            role="alert"
          >
            That sign-in link has expired or is invalid. Please request a new one below.
          </div>
        )}
        <AuthPage />
      </>
    );
  }

  if (upload.phase === "done") {
    return (
      <div
        className="mx-auto max-w-lg rounded-lg border border-ink/10 bg-cream p-8 text-center shadow-stage"
        role="status"
        aria-live="polite"
      >
        <div
          className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-success/10 text-success"
          aria-hidden="true"
        >
          <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="font-serif text-heading text-primary">Upload complete</p>
        <p className="mt-3 text-body text-secondary">
          Thank you. Our team received your files and will follow up at{" "}
          <strong>{email}</strong>.
        </p>
        <p className="mt-2 font-mono text-caption text-muted">Reference: {upload.uploadId}</p>
        <button
          type="button"
          className="mt-6 rounded-md bg-coral px-5 py-2.5 font-mono text-button text-surface transition-opacity duration-fast hover:opacity-90"
          onClick={() => {
            upload.reset();
            queue.clear();
            setNote("");
          }}
        >
          Upload more files
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-heading text-primary">Upload your documents</h1>
          <p className="mt-2 max-w-xl text-body text-secondary">
            Securely share case files for a Canotera proof-of-concept. Review your list
            before uploading — you can rename or remove files first.
          </p>
        </div>
        <div className="text-right text-body-sm">
          <p className="text-secondary">Signed in as</p>
          <p className="font-medium text-primary">{email}</p>
          <button
            type="button"
            className="mt-1 text-caption text-tertiary underline transition-colors duration-fast hover:text-primary"
            onClick={logout}
          >
            Sign out
          </button>
        </div>
      </div>

      <DropZone onFiles={queue.addFiles} disabled={uploading} />

      {queue.items.length > 0 && (
        <>
          <FileQueue
            items={queue.items}
            progressById={upload.progressById}
            uploading={uploading}
            onRemove={queue.remove}
            onRename={queue.rename}
          />

          <div className="flex flex-wrap items-center justify-between gap-4 text-body-sm text-secondary">
            <span>
              {queue.stats.count} file{queue.stats.count === 1 ? "" : "s"} ready ·{" "}
              {formatBytes(queue.stats.totalBytes)}
              {queue.stats.issues > 0 && (
                <span className="text-error"> · {queue.stats.issues} need attention</span>
              )}
            </span>
            <span className="text-muted">Max {UPLOAD_MAX_FILE_SIZE_LABEL} per file</span>
          </div>

          <label className="block">
            <span className="font-mono text-label uppercase text-tertiary">
              Note for our team (optional)
            </span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={uploading}
              rows={3}
              className="mt-1.5 w-full rounded-md border border-ink/15 bg-cream px-3 py-2 text-body-sm shadow-card"
              placeholder="Case context, matter name, or special instructions…"
            />
          </label>

          {uploading && (
            <div className="rounded-lg bg-cream-deep/80 px-4 py-3">
              <div className="flex justify-between font-mono text-label uppercase text-tertiary">
                <span>
                  {upload.phase === "starting"
                    ? "Preparing…"
                    : upload.phase === "finishing"
                      ? "Finalizing…"
                      : "Uploading"}
                </span>
                <span>{upload.overallPct}%</span>
              </div>
              <div
                className="mt-2 h-2 overflow-hidden rounded-full bg-ink/10"
                role="progressbar"
                aria-valuenow={upload.overallPct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Overall upload progress"
              >
                <div
                  className="h-full rounded-full bg-coral transition-all duration-base"
                  style={{ width: `${upload.overallPct}%` }}
                />
              </div>
            </div>
          )}

          {upload.error && (
            <p className="rounded-md bg-error/10 px-4 py-3 text-body-sm text-error" role="alert">
              {upload.error}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled={uploading || queue.stats.count === 0 || queue.stats.issues > 0}
              onClick={handleUpload}
              className="rounded-md bg-coral px-6 py-3 font-mono text-button text-surface transition-opacity duration-fast hover:opacity-90 disabled:opacity-40"
            >
              {uploading ? "Uploading…" : `Upload ${queue.stats.count} file${queue.stats.count === 1 ? "" : "s"}`}
            </button>
            {!uploading && (
              <button
                type="button"
                className="rounded-md border border-ink/15 px-4 py-3 font-mono text-button text-secondary transition-colors duration-fast hover:bg-cream-deep"
                onClick={queue.clear}
              >
                Clear all
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
